import {
  createEmptySnapshot,
  FISHYMAP_BRIDGE_V2_VERSION,
  FISHYMAP_CONTRACT_VERSION,
} from "./map-bridge-contract.js";
import { normalizeCharacterIdentityKey } from "./map-character-presence-contract.js";
import {
  normalizeNullableString,
  normalizeSelectionHistoryBehavior,
  normalizeSelectionPointKind,
  normalizeStatePatch,
} from "./map-bridge-input.js";

const V2_STATE_REVISION_KEYS = Object.freeze(["catalogRev", "statusRev"]);

function cloneJson(value) {
  if (value == null) {
    return value;
  }
  return JSON.parse(JSON.stringify(value));
}

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function normalizeBookmarkDetailsChangedEvent(event) {
  if (event?.version !== FISHYMAP_CONTRACT_VERSION) {
    throw new TypeError(
      `unsupported FishyMap bookmark details event version: ${String(event?.version)}`,
    );
  }
  const bookmarkId = typeof event.bookmarkId === "string" ? event.bookmarkId.trim() : "";
  const worldX = Number(event.worldX);
  const worldY = normalizeWorldCoordinate(event.worldY);
  const worldZ = Number(event.worldZ);
  if (!bookmarkId || !Number.isFinite(worldX) || !Number.isFinite(worldZ)) {
    throw new TypeError("FishyMap bookmark-details-changed event requires bookmark identity");
  }
  if (
    !Object.prototype.hasOwnProperty.call(event, "worldY") ||
    typeof event.worldY !== "number" ||
    worldY == null
  ) {
    throw new TypeError(
      "FishyMap bookmark-details-changed worldY must be a finite number",
    );
  }
  if (
    !Array.isArray(event.layerSamples) ||
    !event.layerSamples.every((sample) => isPlainObject(sample))
  ) {
    throw new TypeError("FishyMap bookmark-details-changed event requires layerSamples");
  }
  if (event.pointLabel != null && typeof event.pointLabel !== "string") {
    throw new TypeError("FishyMap bookmark-details-changed pointLabel must be a string or null");
  }
  if (
    event.zoneRgb != null &&
    (!Number.isSafeInteger(event.zoneRgb) || event.zoneRgb < 0 || event.zoneRgb > 0xffffffff)
  ) {
    throw new TypeError("FishyMap bookmark-details-changed zoneRgb must be an unsigned 32-bit integer");
  }
  return {
    bookmarkId,
    worldX,
    worldY,
    worldZ,
    pointLabel: normalizeNullableString(event.pointLabel),
    layerSamples: cloneJson(event.layerSamples),
    zoneRgb: event.zoneRgb ?? null,
  };
}

function bookmarkIdentityMatches(bookmark, bookmarkId, worldX, worldY, worldZ) {
  return (
    bookmark?.id === bookmarkId &&
    bookmark?.worldX === worldX &&
    bookmark?.worldY === worldY &&
    bookmark?.worldZ === worldZ
  );
}

export function projectBookmarkDetailsChangedEvent(
  currentBookmarks,
  inputBookmarks,
  event,
) {
  const details = normalizeBookmarkDetailsChangedEvent(event);
  const normalizedInputBookmarks =
    normalizeStatePatch({ ui: { bookmarks: inputBookmarks } }).ui?.bookmarks || [];
  const targetIndex = normalizedInputBookmarks.findIndex((bookmark) =>
    bookmarkIdentityMatches(
      bookmark,
      details.bookmarkId,
      details.worldX,
      details.worldY,
      details.worldZ,
    ),
  );
  if (targetIndex < 0) {
    return { applied: false, bookmarks: currentBookmarks };
  }

  const normalizedCurrentBookmarks = Array.isArray(currentBookmarks) ? currentBookmarks : [];
  const runtimeByIdentity = new Map(
    normalizedCurrentBookmarks.map((bookmark) => [
      JSON.stringify([bookmark?.id, bookmark?.worldX, bookmark?.worldY, bookmark?.worldZ]),
      bookmark,
    ]),
  );
  const nextBookmarks = normalizedInputBookmarks.map((bookmark) => {
    const current = runtimeByIdentity.get(
      JSON.stringify([bookmark.id, bookmark.worldX, bookmark.worldY, bookmark.worldZ]),
    );
    if (!current) {
      return bookmark;
    }
    return {
      ...bookmark,
      ...(normalizeNullableString(current.pointLabel)
        ? { pointLabel: normalizeNullableString(current.pointLabel) }
        : {}),
      ...(Array.isArray(current.layerSamples) && current.layerSamples.length
        ? { layerSamples: cloneJson(current.layerSamples) }
        : {}),
      ...(Number.isSafeInteger(current.zoneRgb) && current.zoneRgb >= 0
        ? { zoneRgb: current.zoneRgb }
        : {}),
    };
  });
  nextBookmarks[targetIndex] = {
    ...normalizedInputBookmarks[targetIndex],
    ...(details.pointLabel ? { pointLabel: details.pointLabel } : {}),
    ...(details.layerSamples.length ? { layerSamples: details.layerSamples } : {}),
    ...(details.zoneRgb != null ? { zoneRgb: details.zoneRgb } : {}),
  };
  return { applied: true, bookmarks: nextBookmarks };
}

function projectBookmarkRuntimeDetails(currentState, inputState, event) {
  const projection = projectBookmarkDetailsChangedEvent(
    currentState?.ui?.bookmarks,
    inputState?.ui?.bookmarks,
    event,
  );
  if (!projection.applied) {
    return currentState;
  }
  return {
    ...currentState,
    ui: {
      ...currentState?.ui,
      bookmarks: projection.bookmarks,
    },
  };
}

function normalizeHotspotVisibility(value) {
  const totalCount = value?.totalCount;
  const visibleCount = value?.visibleCount;
  if (
    value?.version != null &&
    value.version !== FISHYMAP_CONTRACT_VERSION
  ) {
    throw new TypeError(
      `unsupported FishyMap hotspot visibility event version: ${String(value.version)}`,
    );
  }
  if (
    !Number.isSafeInteger(totalCount) ||
    totalCount < 0 ||
    !Number.isSafeInteger(visibleCount) ||
    visibleCount < 0 ||
    visibleCount > totalCount
  ) {
    throw new TypeError(
      "FishyMap hotspot visibility requires valid totalCount and visibleCount",
    );
  }
  return { totalCount, visibleCount };
}

export function assertFishyMapV2EventBatch(batch) {
  if (!isPlainObject(batch) || batch.version !== FISHYMAP_BRIDGE_V2_VERSION) {
    throw new TypeError(`unsupported FishyMap event batch version: ${String(batch?.version)}`);
  }
  if (!Array.isArray(batch.events)) {
    throw new TypeError("FishyMap event batch events must be an array");
  }
  if (
    Object.prototype.hasOwnProperty.call(batch, "appliedInputSequence") &&
    (!Number.isSafeInteger(batch.appliedInputSequence) || batch.appliedInputSequence < 0)
  ) {
    throw new TypeError("FishyMap appliedInputSequence must be a non-negative safe integer");
  }
  for (const event of batch.events) {
    if (!isPlainObject(event) || typeof event.type !== "string" || !event.type.trim()) {
      throw new TypeError("FishyMap output event must have a type");
    }
  }
  return batch.events;
}

export function applyV2InputAcknowledgement(current, sent, incoming) {
  if (incoming == null) {
    return { appliedInputSequence: current, advanced: false };
  }
  if (!Number.isSafeInteger(incoming) || incoming < 0) {
    throw new TypeError("FishyMap appliedInputSequence must be a non-negative safe integer");
  }
  if (incoming < current) {
    throw new TypeError("FishyMap appliedInputSequence must not regress");
  }
  if (incoming > sent) {
    throw new TypeError("FishyMap appliedInputSequence must reference a sent input batch");
  }
  return {
    appliedInputSequence: incoming,
    advanced: incoming > current,
  };
}

export function resolvedCurrentViewMode(state) {
  return state?.view?.viewMode === "3d" ? "3d" : "2d";
}

function normalizeWorldCoordinate(value) {
  if (
    value == null ||
    typeof value === "boolean" ||
    (typeof value === "string" && !value.trim())
  ) {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeLandmarkViewportAnchor(value) {
  if (!isPlainObject(value)) {
    return null;
  }
  const x = value.x;
  const y = value.y;
  if (
    typeof x !== "number" ||
    !Number.isFinite(x) ||
    typeof y !== "number" ||
    !Number.isFinite(y)
  ) {
    return null;
  }
  return { x, y };
}

const ROOTED_CDN_PATH_SEGMENT = /^[A-Za-z0-9][A-Za-z0-9._-]*$/;

function normalizeLandmarkMarker(value) {
  if (value == null) {
    return null;
  }
  if (!isPlainObject(value)) {
    throw new TypeError("FishyMap landmarkMarker must be an object");
  }
  const imagePath = value.imagePath;
  const segments = typeof imagePath === "string" ? imagePath.slice(1).split("/") : [];
  if (
    typeof imagePath !== "string" ||
    imagePath !== imagePath.trim() ||
    imagePath.length > 512 ||
    !imagePath.startsWith("/") ||
    imagePath.startsWith("//") ||
    imagePath.includes("\\") ||
    imagePath.includes("?") ||
    imagePath.includes("#") ||
    imagePath.includes("%") ||
    segments.length === 0 ||
    segments.some((segment) => !ROOTED_CDN_PATH_SEGMENT.test(segment))
  ) {
    throw new TypeError(
      "FishyMap landmarkMarker.imagePath must be a canonical rooted CDN path",
    );
  }
  return { imagePath };
}

function normalizeSelectionDetailsGeneration(value) {
  const parsed = Number.parseInt(value, 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : 0;
}

function normalizeSelectionDetailsTarget(value) {
  if (!isPlainObject(value)) {
    return null;
  }
  const worldX = normalizeWorldCoordinate(value.worldX);
  const worldZ = normalizeWorldCoordinate(value.worldZ);
  if (worldX == null || worldZ == null) {
    return null;
  }
  const elementKind = normalizeNullableString(value.elementKind) || "point";
  const pointKind = normalizeSelectionPointKind(value.pointKind);
  const characterIdentityKey =
    value.characterIdentityKey == null
      ? null
      : normalizeCharacterIdentityKey(
          value.characterIdentityKey,
          "detailsTarget.characterIdentityKey",
        );
  if (
    characterIdentityKey != null &&
    (elementKind !== "character" || pointKind !== "character")
  ) {
    throw new TypeError(
      "detailsTarget.characterIdentityKey requires a Character target",
    );
  }
  return {
    elementKind,
    ...(characterIdentityKey ? { characterIdentityKey } : {}),
    worldX,
    worldZ,
    pointKind,
    pointLabel: normalizeNullableString(value.pointLabel),
    historyBehavior: normalizeSelectionHistoryBehavior(value.historyBehavior) || "append",
  };
}

function normalizeZoneRgbs(values) {
  if (!Array.isArray(values)) {
    return [];
  }
  const out = [];
  const seen = new Set();
  for (const value of values) {
    const number = Number(value);
    if (
      !Number.isInteger(number) ||
      number < 0 ||
      number > 0xffffffff ||
      seen.has(number)
    ) {
      continue;
    }
    seen.add(number);
    out.push(number);
  }
  return out;
}

export function normalizeBridgePointSamples(value) {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.flatMap((sample) => {
    if (!isPlainObject(sample)) {
      return [];
    }
    const fishId = Number.parseInt(sample.fishId, 10);
    const sampleCount = Math.max(1, Number.parseInt(sample.sampleCount, 10) || 1);
    const lastTsUtc = Number.parseInt(sample.lastTsUtc, 10);
    const sampleId = Number.parseInt(sample.sampleId, 10);
    if (!Number.isInteger(fishId) || !Number.isInteger(lastTsUtc)) {
      return [];
    }
    return [
      {
        fishId,
        sampleCount,
        lastTsUtc,
        ...(Number.isInteger(sampleId) && sampleId > 0 ? { sampleId } : {}),
        zoneRgbs: normalizeZoneRgbs(sample.zoneRgbs),
        fullZoneRgbs: normalizeZoneRgbs(sample.fullZoneRgbs),
      },
    ];
  });
}

function normalizeHoverLayerSamples(value) {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.flatMap((sample, sampleIndex) => {
    if (!isPlainObject(sample)) {
      return [];
    }
    const normalized = cloneJson(sample);
    delete normalized.characterIdentityKey;
    if (sample.characterIdentityKey == null) {
      return [normalized];
    }
    const characterIdentityKey = normalizeCharacterIdentityKey(
      sample.characterIdentityKey,
      `hover.layerSamples[${sampleIndex}].characterIdentityKey`,
    );
    const ownsCharacterTarget =
      sample.layerId === "live_events" &&
      sample.kind === "character" &&
      Array.isArray(sample.targets) &&
      sample.targets.some(
        (target) => isPlainObject(target) && target.key === "character",
      );
    if (!ownsCharacterTarget) {
      throw new TypeError(
        `hover.layerSamples[${sampleIndex}].characterIdentityKey requires a Character sample target`,
      );
    }
    return [{ ...normalized, characterIdentityKey }];
  });
}

export function normalizeHoverSnapshotValue(value) {
  const layerSamples = normalizeHoverLayerSamples(value?.layerSamples);
  const pointSamples = normalizeBridgePointSamples(value?.pointSamples);
  const landmarkViewportAnchor = normalizeLandmarkViewportAnchor(value?.landmarkViewportAnchor);
  const landmarkMarker = normalizeLandmarkMarker(value?.landmarkMarker);
  return {
    worldX: normalizeWorldCoordinate(value?.worldX),
    worldY: normalizeWorldCoordinate(value?.worldY),
    worldZ: normalizeWorldCoordinate(value?.worldZ),
    layerSamples,
    ...(pointSamples.length ? { pointSamples } : {}),
    ...(landmarkViewportAnchor ? { landmarkViewportAnchor } : {}),
    ...(landmarkMarker ? { landmarkMarker } : {}),
  };
}

export function normalizeSelectionSnapshotValue(value) {
  const layerSamples = Array.isArray(value?.layerSamples) ? cloneJson(value.layerSamples) : [];
  const pointSamples = normalizeBridgePointSamples(value?.pointSamples);
  const normalized = isPlainObject(value) ? cloneJson(value) : {};
  delete normalized.pointSamples;
  delete normalized.detailsGeneration;
  delete normalized.detailsPending;
  delete normalized.detailsTarget;
  const detailsGeneration = normalizeSelectionDetailsGeneration(value?.detailsGeneration);
  if (value?.detailsPending != null && typeof value.detailsPending !== "boolean") {
    throw new TypeError("FishyMap selection detailsPending must be a boolean");
  }
  const hasDetailsPending = Object.prototype.hasOwnProperty.call(
    isPlainObject(value) ? value : {},
    "detailsPending",
  );
  const detailsTarget = normalizeSelectionDetailsTarget(value?.detailsTarget);
  return {
    ...normalized,
    ...(detailsGeneration ? { detailsGeneration } : {}),
    ...(hasDetailsPending ? { detailsPending: value.detailsPending === true } : {}),
    ...(detailsTarget ? { detailsTarget } : {}),
    worldX: normalizeWorldCoordinate(value?.worldX),
    worldY: normalizeWorldCoordinate(value?.worldY),
    worldZ: normalizeWorldCoordinate(value?.worldZ),
    pointKind: normalizeSelectionPointKind(value?.pointKind),
    pointLabel: normalizeNullableString(value?.pointLabel),
    layerSamples,
    ...(pointSamples.length ? { pointSamples } : {}),
  };
}

export function selectionSnapshotFromBridgeEventDetail(detail) {
  if (!isPlainObject(detail)) {
    return null;
  }
  return normalizeSelectionSnapshotValue({
    detailsGeneration: detail.detailsGeneration,
    detailsPending: detail.detailsPending,
    detailsTarget: detail.detailsTarget,
    worldX: detail.worldX,
    worldY: detail.worldY,
    worldZ: detail.worldZ,
    pointKind: detail.pointKind,
    pointLabel: detail.pointLabel,
    layerSamples: detail.layerSamples,
    pointSamples: detail.pointSamples,
    zoneMaskLineage: detail.zoneMaskLineage,
  });
}

export function mergeBootstrapSnapshot(currentState, bootstrapState) {
  const current = currentState || createEmptySnapshot();
  const parsed = bootstrapState || {};
  return {
    ...current,
    version: Number(parsed.version || current.version || FISHYMAP_CONTRACT_VERSION),
    ready: parsed.ready === true,
    statuses: {
      ...createEmptySnapshot().statuses,
      ...(current.statuses || {}),
      ...(parsed.statuses || {}),
    },
  };
}

export function projectFishyMapSnapshotPayload(currentState, kind, snapshot) {
  if (!isPlainObject(snapshot)) {
    throw new TypeError(`map ${kind} snapshot must be an object`);
  }
  if (kind === "bootstrap") {
    return {
      applied: true,
      currentState: mergeBootstrapSnapshot(currentState, snapshot),
    };
  }
  if (kind === "state") {
    const nextState = {
      ...createEmptySnapshot(),
      ...snapshot,
    };
    return {
      applied: true,
      currentState: {
        ...nextState,
        selection: normalizeSelectionSnapshotValue(nextState.selection),
        hover: normalizeHoverSnapshotValue(nextState.hover),
        hotspotVisibility: normalizeHotspotVisibility(nextState.hotspotVisibility),
      },
    };
  }
  return { applied: false, currentState };
}

export function projectFishyMapStateEvent(currentState, inputState, event) {
  if (!isPlainObject(event)) {
    throw new TypeError("FishyMap output event must be an object");
  }
  if (event.type === "hover-changed") {
    return {
      currentState: {
        ...currentState,
        hover: normalizeHoverSnapshotValue(event),
      },
      inputState,
    };
  }
  if (event.type === "view-changed") {
    if (
      (event.viewMode !== "2d" && event.viewMode !== "3d") ||
      !isPlainObject(event.camera)
    ) {
      throw new TypeError("FishyMap view-changed event requires viewMode and camera");
    }
    return {
      currentState: {
        ...currentState,
        view: {
          ...currentState?.view,
          viewMode: event.viewMode,
          camera: cloneJson(event.camera),
        },
      },
      inputState: {
        ...inputState,
        ui: {
          ...inputState?.ui,
          viewMode: event.viewMode,
        },
      },
    };
  }
  if (event.type === "selection-changed") {
    return {
      currentState: {
        ...currentState,
        selection: selectionSnapshotFromBridgeEventDetail(event),
      },
      inputState,
    };
  }
  if (event.type === "bookmark-details-changed") {
    return {
      currentState: projectBookmarkRuntimeDetails(currentState, inputState, event),
      inputState,
    };
  }
  if (event.type === "hotspot-visibility-changed") {
    return {
      currentState: {
        ...currentState,
        hotspotVisibility: normalizeHotspotVisibility(event),
      },
      inputState,
    };
  }
  if (event.type === "ready") {
    return {
      currentState: {
        ...currentState,
        ready: true,
      },
      inputState,
    };
  }
  throw new TypeError(`unsupported FishyMap output event: ${event.type}`);
}

export function applyV2StateRevisions(current, incoming) {
  const previous = isPlainObject(current) ? current : {};
  if (
    !isPlainObject(incoming) ||
    Object.keys(incoming).length !== V2_STATE_REVISION_KEYS.length ||
    !Object.keys(incoming).every((key) => V2_STATE_REVISION_KEYS.includes(key))
  ) {
    throw new TypeError("FishyMap state revisions must contain catalogRev and statusRev");
  }
  const next = {};
  const changed = [];
  for (const key of V2_STATE_REVISION_KEYS) {
    const revision = incoming[key];
    const previousRevision = previous[key] ?? 0;
    if (!Number.isSafeInteger(revision) || revision < 0) {
      throw new TypeError(`FishyMap ${key} must be a non-negative safe integer`);
    }
    if (!Number.isSafeInteger(previousRevision) || previousRevision < 0) {
      throw new TypeError(`cached FishyMap ${key} is invalid`);
    }
    if (revision < previousRevision) {
      throw new TypeError(`FishyMap ${key} must not regress`);
    }
    if (revision !== previousRevision) {
      changed.push(key);
    }
    next[key] = revision;
  }
  return { revisions: next, changed };
}
